import { useState, useEffect } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface Secret { key: string; value: string }
interface EnvFile { path: string; secrets: Secret[] }

export function SecretsPanel({ projectSlug }: { projectSlug?: string } = {}) {
  const [envFiles, setEnvFiles] = useState<EnvFile[]>([])
  const [activeFile, setActiveFile] = useState<string | null>(null)
  const [secrets, setSecrets] = useState<Secret[]>([])
  const [newKey, setNewKey] = useState('')
  const [newValue, setNewValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [projects, setProjects] = useState<string[]>([])
  const [selectedProject, setSelectedProject] = useState<string>(projectSlug || '')

  // Load all .env files and detect projects
  const loadAll = async (keepActive?: string) => {
    try {
      const res = await authFetch(apiUrl('/api/tools/secrets/list'))
      const data = await res.json()
      let files: EnvFile[] = Array.isArray(data) ? data : []

      if (projectSlug) {
        const slug = projectSlug.replace('.html', '')
        // Dashboard scoped view: show only this project's .env
        const projectFiles = files.filter((f) => {
          const p = f.path.replace(/\\/g, '/')
          return p.startsWith(slug + '/') || p.startsWith('.cloud-cache/' + slug + '/')
        })
        if (projectFiles.length > 0) {
          files = projectFiles
        } else {
          // Root HTML file: only show root-level .env files
          files = files.filter((f) => !f.path.replace(/\\/g, '/').includes('/'))
        }
      }
      // When no projectSlug (IDE sidebar), show ALL .env files unfiltered

      setEnvFiles(files)
      const target = keepActive || activeFile
      if (target) {
        const found = files.find((f) => f.path === target)
        if (found) { setActiveFile(found.path); setSecrets(found.secrets) }
        else if (files.length > 0) { setActiveFile(files[0].path); setSecrets(files[0].secrets) }
        else { setActiveFile(null); setSecrets([]) }
      } else if (files.length > 0) {
        setActiveFile(files[0].path)
        setSecrets(files[0].secrets)
      } else {
        setActiveFile(null)
        setSecrets([])
      }
    } catch {}

    // Detect project folders (exclude hidden ones)
    if (!projectSlug) {
      try {
        const res = await authFetch(apiUrl('/api/files/list'))
        if (res.ok) {
          const tree = await res.json()
          const folders = (tree as any[])
            .filter((n: any) => n.type === 'folder' && !n.name.startsWith('.'))
            .map((n: any) => n.name)
          setProjects(folders)
          if (folders.length > 0 && !selectedProject) setSelectedProject(folders[0])
        }
      } catch {}
    }
  }

  useEffect(() => { loadAll() }, [])

  const selectFile = (path: string) => {
    setActiveFile(path)
    const found = envFiles.find((f) => f.path === path)
    setSecrets(found?.secrets ?? [])
  }

  const save = async (updated: Secret[]) => {
    if (!activeFile) return
    setSaving(true)
    await authFetch(apiUrl('/api/tools/secrets'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: activeFile, secrets: updated }),
    }).catch(() => {})
    setSecrets(updated)
    setSaving(false)
    loadAll(activeFile)
  }

  const add = () => {
    if (!newKey.trim()) return
    // If no active file, create one in the right place
    if (!activeFile) {
      const slug = projectSlug?.replace('.html', '') || ''
      const isRootFile = projectSlug && !projects.includes(slug)
      const path = isRootFile ? '.env' : (selectedProject ? `${selectedProject}/.env` : '.env')
      createEnvFile(path, [{ key: newKey.trim(), value: newValue }])
    } else {
      save([...secrets, { key: newKey.trim(), value: newValue }])
    }
    setNewKey(''); setNewValue('')
  }

  const remove = (idx: number) => save(secrets.filter((_, i) => i !== idx))
  const update = (idx: number, field: 'key' | 'value', val: string) => setSecrets(secrets.map((s, i) => i === idx ? { ...s, [field]: val } : s))
  const blur = () => save(secrets)

  const createEnvFile = async (customPath?: string, initialSecrets?: Secret[]) => {
    // For root HTML files, always create at root .env
    const slug = projectSlug?.replace('.html', '') || ''
    const isRootFile = projectSlug && !projects.includes(slug)
    const p = customPath ?? (isRootFile ? '.env' : (selectedProject ? `${selectedProject}/.env` : '.env'))
    const content = initialSecrets
      ? initialSecrets.map((s) => `${s.key}=${s.value}`).join('\n') + '\n'
      : '# Environment Variables\n'

    await authFetch(apiUrl('/api/files/write'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path: p, content }),
    })
    setActiveFile(p)
    await loadAll(p)
    window.dispatchEvent(new CustomEvent('pulse:files-changed'))
  }

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <span>SECRETS (.env)</span>
        {saving && <span style={{ fontSize: 8, color: 'var(--accent-2)' }}>saving...</span>}
      </div>

      {/* File selector */}
      <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        {envFiles.length > 0 ? (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {envFiles.map((f) => (
              <button key={f.path} onClick={() => selectFile(f.path)} style={{
                padding: '3px 8px', borderRadius: 4, fontSize: 10, fontFamily: 'var(--font-mono)',
                border: activeFile === f.path ? '1px solid var(--accent)' : '1px solid var(--border-2)',
                background: activeFile === f.path ? 'rgba(124,110,245,0.1)' : 'none',
                color: activeFile === f.path ? 'var(--accent)' : 'var(--text-2)', cursor: 'pointer',
              }}>{f.path}</button>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>No .env files found</div>
        )}

        {/* Create new .env */}
        <div style={{ display: 'flex', gap: 4, marginTop: 6, alignItems: 'center' }}>
          {!projectSlug && projects.length > 0 && (
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              style={{ ...inputStyle, width: 'auto', padding: '3px 6px' }}
            >
              {projects.map((p) => <option key={p} value={p}>{p}/</option>)}
              <option value="">root</option>
            </select>
          )}
          <button onClick={() => createEnvFile()} style={addBtn}>
            + New .env
          </button>
        </div>
      </div>

      {/* Secrets list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {!activeFile ? (
          <div style={emptyStyle}>
            <span style={{ fontSize: 24 }}>🔐</span>
            <span>Create a .env file to get started</span>
            <span style={{ fontSize: 10, color: 'var(--text-2)', textAlign: 'center', maxWidth: 200 }}>
              Select a project above and click "+ New .env" to create secrets inside your project folder
            </span>
          </div>
        ) : secrets.length === 0 ? (
          <div style={emptyStyle}>
            <span style={{ fontSize: 24 }}>🔐</span>
            <span>No secrets in {activeFile}</span>
            <span style={{ fontSize: 10, color: 'var(--text-2)' }}>Add variables below</span>
          </div>
        ) : secrets.map((s, i) => (
          <div key={i} style={{ padding: '4px 14px', display: 'flex', gap: 6, alignItems: 'center' }}>
            <input value={s.key} onChange={(e) => update(i, 'key', e.target.value)} onBlur={blur} placeholder="KEY" style={{ ...inputStyle, width: 100, fontWeight: 700 }} />
            <span style={{ color: 'var(--text-2)' }}>=</span>
            <input value={s.value} onChange={(e) => update(i, 'value', e.target.value)} onBlur={blur} placeholder="value" style={{ ...inputStyle, flex: 1 }} type="password" onFocus={(e) => e.target.type = 'text'} onBlurCapture={(e) => e.target.type = 'password'} />
            <button onClick={() => remove(i)} style={delBtn}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        ))}
      </div>

      {/* Add new */}
      {activeFile && (
        <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border)', display: 'flex', gap: 6, flexShrink: 0 }}>
          <input value={newKey} onChange={(e) => setNewKey(e.target.value.toUpperCase())} placeholder="KEY" style={{ ...inputStyle, width: 100, fontWeight: 700 }} onKeyDown={(e) => { if (e.key === 'Enter') add() }} />
          <input value={newValue} onChange={(e) => setNewValue(e.target.value)} placeholder="value" style={{ ...inputStyle, flex: 1 }} onKeyDown={(e) => { if (e.key === 'Enter') add() }} />
          <button onClick={add} disabled={!newKey.trim()} style={addBtn}>+ Add</button>
        </div>
      )}

      {/* Info */}
      <div style={{ padding: '6px 12px', borderTop: '1px solid var(--border)', fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
        💡 Secrets are saved inside your project folder. Restart the dev server after adding new keys.
      </div>
    </div>
  )
}

const panelStyle: React.CSSProperties = { width: '100%', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-1)', borderRight: '1px solid var(--border)', overflow: 'hidden' }
const headerStyle: React.CSSProperties = { padding: '8px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--text-2)', textTransform: 'uppercase', borderBottom: '1px solid var(--border)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }
const emptyStyle: React.CSSProperties = { padding: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: 'var(--text-2)', fontSize: 11, fontFamily: 'var(--font-mono)' }
const inputStyle: React.CSSProperties = { padding: '4px 8px', borderRadius: 4, border: '1px solid var(--border-2)', background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontSize: 11, outline: 'none' }
const delBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', display: 'flex', padding: 4, borderRadius: 4 }
const addBtn: React.CSSProperties = { padding: '4px 10px', borderRadius: 4, border: '1px solid var(--accent)', background: 'rgba(124,110,245,0.08)', color: 'var(--accent)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }
